/**
 * Created by jonas on 2022-06-15.
 */
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {gsap} from "gsap";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {KitchenDramaSushiMania} from "../KitchenDramaSushiMania";
import {KitchenDramaSushiManiaGameAssets} from "../KitchenDramaSushiManiaGameAssets";
import {DevKeyTriggerManager} from "@nolimitcity/slot-launcher/bin/devtools/DevKeyTriggerManager";
import {KitchenDramaSushiManiaSound} from "../sound/KitchenDramaSushiManiaSound";

export class KamisabiSplashView extends PIXI.Container implements StageLayer {
    private _view:PIXI.Container;
    private _banner:PIXI.Sprite;
    private _bgAnimation:TimelineSprite;
    private _textAnimation:TimelineSprite;
    private _ka:TimelineSprite;
    private _mi:TimelineSprite;
    private _sabi:TimelineSprite;
    private _darkPlate:PIXI.Sprite;
    private _loops:gsap.core.Timeline[] = [];
    private scaleMod:number = 1;


    constructor() {
        super();
        this.name = "kamisabiSplashView";
        this.init();
    }

    private init() {
        this._view = new PIXI.Container();
        this._view.position.set(360, 320);


        //Banner
        let totalBannerHeight = 380;

        //Bottom
        this._darkPlate = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._darkPlate.tint = 0x000000;
        this._darkPlate.anchor.set(0.5, 0.5);

        //Bottom
        this._banner = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._banner.tint = 0x352343;
        this._banner.height = totalBannerHeight;
        this._banner.anchor.set(0.5, 0.5);


        //Animated background
        this._bgAnimation = new TimelineSprite(KitchenDramaSushiMania.assetLoader.getTextures(KitchenDramaSushiManiaGameAssets.KAMISABI_BACKGROUND_ANIMATION));
        this._bgAnimation.anchor.set(0.5, 0.5);


        //Kamisabi text
        this._textAnimation = new TimelineSprite(KitchenDramaSushiMania.assetLoader.getTextures(KitchenDramaSushiManiaGameAssets.KAMISABI_TEXT_ANIMATION));
        this._textAnimation.hide();
        this._textAnimation.anchor.set(0.5, 0.5);
        this._textAnimation.position.set(0, -175);


        //Kamisabi dudes
        this._ka = new TimelineSprite(KitchenDramaSushiMania.assetLoader.getTextures(KitchenDramaSushiManiaGameAssets.KAMISABI_ANIMATION_KA));
        this._ka.anchor.set(0.5, 0.5);
        this._mi = new TimelineSprite(KitchenDramaSushiMania.assetLoader.getTextures(KitchenDramaSushiManiaGameAssets.KAMISABI_ANIMATION_MI));
        this._mi.anchor.set(0.5, 0.5);
        this._sabi = new TimelineSprite(KitchenDramaSushiMania.assetLoader.getTextures(KitchenDramaSushiManiaGameAssets.KAMISABI_ANIMATION_SABI));
        this._sabi.anchor.set(0.5, 0.7);


        this._view.addChild(this._darkPlate);
        this._view.addChild(this._banner);
        this._view.addChild(this._bgAnimation);
        this._view.addChild(this._textAnimation);
        this._view.addChild(this._ka);
        this._view.addChild(this._mi);
        this._view.addChild(this._sabi);

        this.reset();
    }

    onResize():void {
        if (this._view.parent) {

            if (NolimitApplication.isLandscape) {
                this.scaleMod = 1;
            } else {
                this.scaleMod = 0.65;
            }

            this._view.scale.set(this.scaleMod,this.scaleMod);
            this._banner.width = NolimitApplication.screenBounds.width / this.scaleMod;
            this._darkPlate.width = NolimitApplication.screenBounds.width / this.scaleMod;
            this._darkPlate.height = NolimitApplication.screenBounds.height / this.scaleMod;
            this._darkPlate.position.y = 360 - this._view.position.y;

            this._bgAnimation.width = NolimitApplication.screenBounds.width / this.scaleMod;


        }
    }


    public show():gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(() => {
            this.addChild(this._view);
            this.onResize();
        });

        tl.add(() => KitchenDramaSushiMania.soundPlayer.playSound(KitchenDramaSushiManiaSound.KAMISABI_SPLASH));
        tl.add(() => KitchenDramaSushiMania.soundPlayer.fadeOutMusic());
        tl.add(this.createBannerAnimation());
        tl.add(this.createTextAndDudesAnimation(), "-=0.4");
        tl.add(this.createBannerOutAnimation(), "-=0.1");
        tl.add(() => {
            this.removeChild(this._view);
            this.stopLoops();
            this.reset();
        });
        tl.add(() => KitchenDramaSushiMania.soundPlayer.fadeInMusic());

        return tl;
    }

    public hide(duration:number = 0.8):gsap.core.Timeline {
        const tl = gsap.timeline();

        tl.add(() => {
            this.removeChild(this._view);
        });
        return tl;
    }


    private createBannerAnimation():gsap.core.Timeline {
        let tl:gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            this._bgAnimation.show();
        });
        tl.add([
            gsap.fromTo(this._darkPlate, {alpha:0}, {duration:1, alpha:0.6}),
            this._bgAnimation.getAnimation([0, 31])
        ]);
        tl.add([
            () => {
                this._banner.visible = true
            },
            gsap.fromTo(this._banner, {height:0}, {height:380, ease:"power4.out"})
        ], ">-0.5");
        tl.add(() => this.startLoop(this._bgAnimation, 31));

        return tl;
    }

    private createTextAndDudesAnimation():gsap.core.Timeline {
        let tl:gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            this._textAnimation.show();
        });
        tl.add(gsap.fromTo(this._textAnimation.position, {x:-NolimitApplication.screenBounds.width / this.scaleMod}, {
            duration:0.1,
            x:-280,
            ease:"none"
        }));
        let textAnimStart = tl.totalDuration();
        tl.add(this._textAnimation.getAnimation([0, 25]));
        tl.add(() => this.startLoop(this._textAnimation, 26));

        tl.add(this.createDudeAnimation(this._ka, KitchenDramaSushiManiaSound.KAMISABI_KA, 70, 2.8), textAnimStart);
        tl.add(this.createDudeAnimation(this._mi, KitchenDramaSushiManiaSound.KAMISABI_MI, 40, 2.5), textAnimStart + 0.3);
        tl.add(this.createDudeAnimation(this._sabi, KitchenDramaSushiManiaSound.KAMISABI_SABI, 100, 2), textAnimStart + 0.73);

        return tl;
    }

    private createDudeAnimation(sprite:TimelineSprite, sound:string, paralaxOffset:number, paralaxDuration:number):gsap.core.Timeline {
        let tl:gsap.core.Timeline = gsap.timeline();

        tl.add(() => KitchenDramaSushiMania.soundPlayer.playSound(sound));
        tl.add(() => this.startLoop(sprite));
        tl.add([
            gsap.from(sprite.scale, 0.2, {y:0.3, ease:"power4.out"}),
            gsap.from(sprite, 0.2, {x:"-=" + (NolimitApplication.screenBounds.width / this.scaleMod), ease:"power4.out"})
        ]);

        tl.add(gsap.to(sprite.position, {
            duration:paralaxDuration,
            x:"+=" + paralaxOffset,
            ease:"none"
        }), "-=0.05");

        tl.add([
            () => KitchenDramaSushiMania.soundPlayer.playSound(KitchenDramaSushiManiaSound.KAMISABI_TAKEOFF),
            gsap.to(sprite.scale, {duration:0.15, y:0.3, ease:"power4.in"}),
            gsap.to(sprite, {
                duration:0.15,
                x:NolimitApplication.screenBounds.width / this.scaleMod + sprite.width / 2,
                ease:"power4.in"
            })
        ], "-=0.05");

        return tl;
    }

    private createBannerOutAnimation():gsap.core.Timeline {
        let tl:gsap.core.Timeline = gsap.timeline();

        tl.add([
            gsap.to(this._textAnimation, {duration:0.1, alpha:0, ease:"power4.in"}),
            gsap.to(this._banner.scale, {duration:0.3, y:0, ease:"power4.in"}),
            gsap.to(this._bgAnimation.scale, {duration:0.3, y:0, ease:"power4.in"}),
            gsap.to(this._darkPlate, {duration:0.3, alpha:0, ease:"none"}),

        ]);

        return tl;
    }


    private startLoop(anim:TimelineSprite, from?:number, to?:number) {
        from = from == undefined ? 0 : from;
        to = to == undefined ? anim.totalFrames - 1 : to;
        const loop = anim.getAnimation([from, to]);
        loop.repeat(-1);

        this._loops.push(loop);

    }

    private stopLoops() {
        for (let loop of this._loops) {
            loop.kill();
        }
        this._loops = [];
    }

    private reset() {
        this._ka.position.set(-260, 30);
        this._mi.position.set(90, -115);
        this._sabi.position.set(140, 130);

        this._ka.scale.set(1.2, 1.2);
        this._mi.scale.set(1, 1);
        this._sabi.scale.set(1.1, 1.1);

        this._ka.setFrame(0);
        this._mi.setFrame(0);
        this._sabi.setFrame(0);

        this._bgAnimation.scale.set(1, 1);
        this._bgAnimation.setFrame(0);

        this._textAnimation.alpha = 1;
        this._textAnimation.setFrame(0);
    }
}